/**
 * The sidebar tree's state: expanded folders (persisted per workspace), the
 * folder the route has open (highlighted, its ancestors revealed), the
 * flattened rows, the roving tab stop and the tree keyboard. Opening goes
 * through the canvas's own `useItemActions`: a folder at its BARE path (no
 * carried query string; the canvas fills in the remembered workspace-wide
 * view, sort and filters), a file through the shared entity navigation.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { ROUTES } from '../../../constants/routes';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { useIsReadOnly } from '../../../hooks/user/useWorkspaceRole';
import { useFolderPath } from '../../../hooks/workspace/useFolderPath';
import { useItemActions } from '../../workspace/views/grid/useItemActions';
import { useFolderTreeRows, type TreeRow } from './useFolderTreeRows';
import { useSidebarExpanded } from './useSidebarExpanded';
import { useTreeKeyboard } from './useTreeKeyboard';

function useCurrentFolderId(): string | null {
  const { pathname } = useLocation();
  return matchPath(ROUTES.collection.pattern, pathname)?.params.projectId ?? null;
}

/** Expands the open folder's ancestors once per folder, for this session. */
function useRevealCurrent(currentId: string | null, reveal: (ids: string[]) => void) {
  const { path } = useFolderPath(currentId);
  const ancestors = path.filter((folder) => folder.id !== currentId).map((folder) => folder.id);
  const key = `${currentId ?? ''}:${ancestors.join('/')}`;
  const revealedRef = useRef<string | null>(null);
  if (ancestors.length > 0 && revealedRef.current !== key) {
    revealedRef.current = key;
    reveal(ancestors);
  }
}

export function useFolderTree() {
  const workspaceId = useWorkspaceId() ?? undefined;
  const isReadOnly = useIsReadOnly();
  const { expanded, setOpen, reveal } = useSidebarExpanded(workspaceId);
  const currentId = useCurrentFolderId();
  useRevealCurrent(currentId, reveal);
  const { rows, entries, loadingMore, showMore } = useFolderTreeRows(expanded, !isReadOnly);
  const items = useMemo(() => rows.map((row) => row.item), [rows]);
  const { open } = useItemActions(items, 'tree');

  const [activeId, setActiveId] = useState<string | null>(null);
  const indexOf = (id: string | null) => (id ? rows.findIndex((r) => r.item.id === id) : -1);
  const activeIndex = Math.max(0, [activeId, currentId].map(indexOf).find((i) => i >= 0) ?? 0);

  // Set by a keyboard move: the tree then scrolls to the row and focuses it.
  const focusPending = useRef(false);
  const moveTo = useCallback((id: string) => {
    focusPending.current = true;
    setActiveId(id);
  }, []);

  const setRowExpanded = useCallback(
    (row: TreeRow, isOpen: boolean) => setOpen(row.item.id, isOpen),
    [setOpen],
  );
  const toggle = useCallback(
    (row: TreeRow) => setRowExpanded(row, !row.isExpanded),
    [setRowExpanded],
  );

  const onKeyDown = useTreeKeyboard({
    rows,
    activeIndex,
    setActiveId: moveTo,
    setRowExpanded,
    openRow: open,
  });

  return {
    rows,
    entries,
    loadingMore,
    showMore,
    moveTo,
    items,
    currentId,
    activeIndex,
    focusPending,
    setActiveId,
    open,
    toggle,
    onKeyDown,
  };
}
